// store/college/progress-tracking/_saga.js

import { call, put, takeLatest, select } from 'redux-saga/effects';
import * as actionTypes from './_actionTypes';
import * as actions from './_actions';
import CollegeStudentHelper from '../../../../utils/API/BackendHelper/college/student';
import { buildReportFilename } from '../../../../utils/Export/exportUtils';

/**
 * Fetch students progress
 */
function* fetchStudentProgressSaga(action) {
  try {
    const filters = action.payload || {};

    // Build query parameters - all filters are server-side
    const params = {
      page: filters.page || 1,
      limit: filters.limit || 20,
    };

    if (filters.courseId) params.courseId = filters.courseId;
    if (filters.studentId) params.studentId = filters.studentId;
    if (filters.search) params.search = filters.search;
    if (filters.degree && filters.degree !== 'all') params.degree = filters.degree;
    if (filters.batch) params.batch = filters.batch;
    if (filters.semester && filters.semester !== 'all') params.semester = filters.semester;
    if (filters.progressLevel && filters.progressLevel !== 'all') params.progressLevel = filters.progressLevel;

    const response = yield call(
      CollegeStudentHelper.getStudentsProgress,
      params
    );

    if (response.success) {
      yield put(actions.fetchStudentProgressSuccess(response.data));
    } else {
      throw new Error(response.message || 'Failed to fetch student progress');
    }
  } catch (error) {
    console.error('Fetch student progress error:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch student progress';
    
    yield put(actions.fetchStudentProgressFailure(errorMessage));
  }
}

/**
 * Fetch single student progress
 */
function* fetchSingleStudentProgressSaga(action) {
  try {
    const studentId = action.payload;

    const response = yield call(
      CollegeStudentHelper.getStudentById,
      studentId
    );

    if (response.success) {
      // Also fetch the detailed progress
      const progressResponse = yield call(
        CollegeStudentHelper.getStudentsProgress,
        { studentId }
      );

      if (progressResponse.success && progressResponse.data.students.length > 0) {
        yield put(actions.fetchSingleStudentProgressSuccess(progressResponse.data.students[0]));
      } else {
        yield put(actions.fetchSingleStudentProgressSuccess(response.data));
      }
    } else {
      throw new Error(response.message || 'Failed to fetch student details');
    }
  } catch (error) {
    console.error('Fetch single student progress error:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch student details';
    
    yield put(actions.fetchSingleStudentProgressFailure(errorMessage));
  }
}

/**
 * Export progress report
 */
function* exportProgressReportSaga(action) {
  try {
    const { filters, format } = action.payload;

    // Build query parameters - no page/limit so the service returns all students
    const params = {};

    if (filters.courseId) params.courseId = filters.courseId;
    if (filters.studentId) params.studentId = filters.studentId;
    if (filters.search) params.search = filters.search;
    if (filters.degree && filters.degree !== 'all') params.degree = filters.degree;
    if (filters.batch) params.batch = filters.batch;
    if (filters.semester && filters.semester !== 'all') params.semester = filters.semester;
    if (filters.progressLevel && filters.progressLevel !== 'all') params.progressLevel = filters.progressLevel;

    const response = yield call(
      CollegeStudentHelper.getStudentsProgress,
      params
    );

    if (response.success) {
      // Convert to CSV if format is csv. The filename encodes the applied
      // filters (params holds only the active, non-'all' filters).
      if (format === 'csv') {
        const csvData = convertToCSV(response.data.students);
        downloadCSV(csvData, buildReportFilename('student_progress_report', params, 'csv'));
      } else {
        // Download as JSON
        const jsonData = JSON.stringify(response.data, null, 2);
        downloadJSON(jsonData, buildReportFilename('student_progress_report', params, 'json'));
      }

      yield put(actions.exportProgressReportSuccess());
    } else {
      throw new Error(response.message || 'Failed to export report');
    }
  } catch (error) {
    console.error('Export progress report error:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Failed to export report';
    
    yield put(actions.exportProgressReportFailure(errorMessage));
  }
}

/**
 * Escape a value for CSV (handles commas, quotes, newlines)
 */
function escapeCSV(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Helper function to convert data to CSV
 */
function convertToCSV(students) {
  const headers = [
    'Student ID',
    'First Name',
    'Last Name',
    'Email',
    'Phone',
    'Registration No',
    'Degree',
    'Semester',
    'Batch',
    'Admission Type',
    'Total Courses',
    'Total Contents',
    'Completed Contents',
    'Overall Progress (%)',
    'Total Time Spent (min)',
    'Courses Completed'
  ];

  const rows = students.map(({ student, overall_statistics }) => [
    student.id,
    student.first_name,
    student.last_name || '',
    student.email || '',
    student.phone || '',
    student.registration_no || '',
    student.degree || '',
    student.current_semester || '',
    student.batch || '',
    student.admission_type || '',
    overall_statistics.totalCourses,
    overall_statistics.totalContents,
    overall_statistics.completedContents,
    overall_statistics.overallProgress,
    overall_statistics.totalTimeSpent,
    overall_statistics.coursesCompleted
  ]);

  const csvContent = [
    headers.map(escapeCSV).join(','),
    ...rows.map(row => row.map(escapeCSV).join(','))
  ].join('\n');

  return csvContent;
}

/**
 * Helper function to download CSV
 */
function downloadCSV(csvData, filename) {
  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Helper function to download JSON
 */
function downloadJSON(jsonData, filename) {
  const blob = new Blob([jsonData], { type: 'application/json' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Root saga
 */
export default function* progressTrackingSaga() {
  yield takeLatest(actionTypes.FETCH_STUDENT_PROGRESS_REQUEST, fetchStudentProgressSaga);
  yield takeLatest(actionTypes.FETCH_SINGLE_STUDENT_PROGRESS_REQUEST, fetchSingleStudentProgressSaga);
  yield takeLatest(actionTypes.EXPORT_PROGRESS_REPORT_REQUEST, exportProgressReportSaga);
}